import { useEffect, useRef, useSyncExternalStore } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { RealtimeEvent } from '@fas/types';
import { refreshAccessToken } from './api';
import { useAuthStore } from './auth-store';

/*
 * Socket.IO client (§30.3, §48). The connection authenticates with the in-memory access
 * token; the server places it only in rooms the user is authorized for. Events are hints to
 * refetch authoritative data — never a source of truth on their own.
 */

let socket: Socket | null = null;

const AUTH_ERRORS = new Set(['TOKEN_EXPIRED', 'TOKEN_INVALID', 'UNAUTHENTICATED', 'SESSION_REVOKED']);

function ensureSocket(): Socket {
  if (socket) return socket;
  const s = io({
    path: '/socket.io',
    autoConnect: false,
    // WebSocket first: one long-lived connection, so several API instances behind a load
    // balancer need no sticky sessions; HTTP long-polling remains the fallback — also where the
    // host cannot pass WebSockets through (shared hosting, cpanel.md), which only tryAllTransports reaches.
    transports: ['websocket', 'polling'],
    tryAllTransports: true,
    auth: (cb) => cb({ token: useAuthStore.getState().accessToken }),
  });
  s.on('connect_error', (err) => {
    const code = (err as Error & { data?: { code?: string } }).data?.code;
    if (code && AUTH_ERRORS.has(code) && useAuthStore.getState().status === 'authenticated') {
      void refreshAccessToken().then((ok) => {
        if (ok) s.connect();
      });
    }
  });
  s.on('disconnect', (reason) => {
    // The server closes the connection when the access token expires: reconnect with a fresh one.
    if (reason === 'io server disconnect' && useAuthStore.getState().status === 'authenticated') {
      void refreshAccessToken().then((ok) => {
        if (ok) s.connect();
      });
    }
  });
  socket = s;
  return s;
}

/** Keeps the connection open while signed in. Mount once in the authenticated shell. */
export function useRealtimeConnection(): void {
  const status = useAuthStore((st) => st.status);
  useEffect(() => {
    const s = ensureSocket();
    if (status === 'authenticated') {
      if (!s.connected) s.connect();
    } else {
      s.disconnect();
    }
  }, [status]);
}

function subscribeStatus(onChange: () => void): () => void {
  const s = ensureSocket();
  s.on('connect', onChange);
  s.on('disconnect', onChange);
  return () => {
    s.off('connect', onChange);
    s.off('disconnect', onChange);
  };
}

/** Whether live updates are currently connected (shown as a text indicator, never color alone). */
export function useRealtimeStatus(): boolean {
  return useSyncExternalStore(subscribeStatus, () => ensureSocket().connected, () => false);
}

/** Runs `handler` for the given events while the component is mounted. */
export function useRealtimeEvent<T = unknown>(events: RealtimeEvent | RealtimeEvent[], handler: (payload: T) => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  const key = Array.isArray(events) ? events.join('|') : events;
  useEffect(() => {
    const s = ensureSocket();
    const list = key.split('|');
    const listener = (payload: T) => ref.current(payload);
    for (const e of list) s.on(e, listener);
    return () => {
      for (const e of list) s.off(e, listener);
    };
  }, [key]);
}

/**
 * Calls `refresh` `delayMs` after one of `events` arrives. Further events during the wait are
 * folded into that refresh and do not restart it, so a steady stream (a class-start rush)
 * still refreshes every `delayMs` instead of waiting for a quiet moment (§58.1: dashboard
 * propagation < 3 s). Keep `delayMs` above the server's DASHBOARD_CACHE_MS (1 s) so the
 * refetch after an event always sees it.
 */
export function useRealtimeRefresh(events: RealtimeEvent | RealtimeEvent[], refresh: () => void, delayMs: number): void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useRealtimeEvent(events, () => {
    if (timer.current !== null) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      refreshRef.current();
    }, delayMs);
  });
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    },
    [],
  );
}
